/*
 * camera-reader/read-aloud.js — accessible text-to-speech for the camera reader.
 *
 * Ports the battle-tested read-aloud patterns from document-reader.blade.php,
 * kept self-contained so this tool never depends on that blade:
 *   - per-SENTENCE utterances (Chrome silently cancels one utterance after ~15s)
 *   - karaoke word highlight via utterance.onboundary, mapping charIndex back to
 *     per-word <span data-start> tokens; graceful degradation to sentence-only
 *     highlight (Android never fires `boundary`, Chrome remote voices don't either)
 *   - voice chosen by the TEXT language (not the page locale), local voices first
 *   - cancel-based pause/resume (synth.pause() leaves speaking=true / wedges iOS)
 *   - Chrome-desktop ~15s keep-alive: window.__speechKeepAlive(u) (timbrica-core) —
 *     network voices off Android only; on Android pause() is a STOP with no end
 *     event, and a free-running ping here cut speech after a few words (2026-10-09)
 *   - a module-scope utterance reference so Chrome can't GC it mid-speech
 *   - phantom-speech watchdog via window.SpeechEngineProbe (prod's #1 TTS failure
 *     mode: 236 phantom_speech / 90d on document-reader) → auto-fallback voice
 *
 * HD voices (2026-09-17). What stood here — "deliberately NOT wired" — was about
 * SUPERTONIC, a 190 MB browser download on a shared daily pot, and those reasons
 * still hold for it. They do not hold for the SERVER lane: our own Silero daemon
 * (MIT) downloads nothing, answers in ~0.4 s, and speaks native Russian, which is
 * the whole point of a tool whose users listen to a page they cannot read.
 * So the server lane is wired — as a CHOICE, never as the default:
 *   · Web Speech stays the default. It is instant, works offline, and is the only
 *     lane that can highlight word by word (the HD endpoint returns audio and no
 *     timings; see hd-voice.js for why inventing them was rejected).
 *   · Every HD refusal — daily pot, 5-per-minute rate, busy daemon, offline —
 *     falls back here and FINISHES the passage aloud. A blind user who pressed
 *     "read aloud" must never be answered with silence.
 *
 * Container-only scroll (block:'nearest' / element.scrollTop) — never page scroll,
 * per the project no-auto-scroll rule.
 *
 * Public: window.CRRead = { init, setText, play, pause, stop, toggle, isActive, clear }
 */
(function () {
  'use strict';

  var synth = window.speechSynthesis || null;
  var PROBE = window.SpeechEngineProbe || null;

  var cfg = { i18n: {}, langPrefix: 'en', speechLang: 'en-US' };
  var els = {};                 // reading, textarea, play, stop, voice, speed, speedVal
  var sentences = [];           // [{text, el}]
  var utterance = null;         // module-scope → not GC'd mid-speech
  var currentIndex = 0;
  var isPlaying = false, isPaused = false;
  var boundaryFired = false;    // did onboundary ever fire? (drives karaoke vs sentence-only)
  var watchdog = null;
  var coldNext = true;          // next utterance meets a cold engine (play, resume, new voice, retry)
  var retriedAt = -1;           // sentence that already had its one same-voice retry
  var triedVoices = {};         // lang → count of fallback attempts this session
  var onStateCb = null;
  var onVoicesCb = null;   // the page re-reads the picker after the list is built

  // ── i18n helper ──
  function t(k, d) { return (cfg.i18n && cfg.i18n[k]) || d || k; }

  // ── announce to SR ──
  function announce(msg) { try { if (window.__announce) window.__announce(msg); } catch (e) {} }

  // ── view: editable textarea (idle) ↔ karaoke reading surface (playing) ──
  function showReading(on) {
    if (els.reading) els.reading.hidden = !on;
    if (els.textarea) els.textarea.hidden = on;
  }

  // ── Sentence splitting (document-reader splitSentences) ──
  function splitSentences(text) {
    var raw = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    // sentence-ending punctuation (Latin + CJK) followed by whitespace, or blank lines
    /* ⚠️ Без regex-lookbehind: старый WebKit (iOS до 16.4 — Safari, Chrome и Yandex на
       iOS живут на одном движке) не разбирает такую регулярку и роняет ВЕСЬ файл на
       этапе парсинга (Sentry HE: «invalid group specifier name»). Сентинел после
       разделителя даёт тот же результат, что split с lookbehind: разделитель остаётся у
       предыдущего куска, пробелы после него снимаются. */
    var parts = raw.replace(/([.!?。！？…])\s+/g, '$1\u0001').replace(/\n{2,}/g, '\u0001').split('\u0001');
    var out = [];
    parts.forEach(function (p) {
      p = p.trim();
      if (!p) return;
      // very long lines with no punctuation → split on single newlines
      if (p.length > 240 && /\n/.test(p)) {
        p.split(/\n+/).forEach(function (q) { q = q.trim(); if (q) out.push(q); });
      } else out.push(p);
    });
    if (out.length <= 1 && raw.trim().length > 300) {
      out = raw.split(/\n+/).map(function (s) { return s.trim(); }).filter(Boolean);
    }
    if (out.length === 0 && raw.trim()) out = [raw.trim()];
    return out;
  }

  // ── Render karaoke spans into the reading surface ──
  function render() {
    var wrap = els.reading;
    if (!wrap) return;
    wrap.innerHTML = '';
    sentences.forEach(function (s, i) {
      var sEl = document.createElement('span');
      sEl.className = 'cr-sentence';
      sEl.dataset.index = i;
      sEl.setAttribute('role', 'button');
      sEl.tabIndex = 0;
      sEl.setAttribute('aria-label', t('read_sentence', 'Sentence') + ' ' + (i + 1));
      // word tokens carry cumulative char offset for onboundary mapping
      var toks = s.text.split(/(\s+)/);
      var off = 0;
      toks.forEach(function (tok) {
        if (/^\s+$/.test(tok)) {
          sEl.appendChild(document.createTextNode(tok));
        } else if (tok) {
          var w = document.createElement('span');
          w.className = 'cr-word';
          w.dataset.start = off;
          w.textContent = tok;
          sEl.appendChild(w);
        }
        off += tok.length;
      });
      sEl.appendChild(document.createTextNode(' '));
      s.el = sEl;
      wrap.appendChild(sEl);
    });
  }

  // ── Voice selection: match the TEXT language, local voices first ──
  function pickVoice(langPrefix) {
    if (!synth) return null;
    var pool = (PROBE && PROBE.getVoicesForLang) ? PROBE.getVoicesForLang(langPrefix) : [];
    if (!pool.length) {
      // no exact-lang voice — let the OS pick by utterance.lang
      return null;
    }
    // rank: user-chosen (if valid) → local → "natural/google/premium" → name
    var chosen = els.voice && els.voice.value;
    if (chosen) {
      var all = synth.getVoices();
      var byUri = all.filter(function (v) { return v.voiceURI === chosen; })[0];
      if (byUri) return byUri;
    }
    pool.sort(function (a, b) {
      var al = a.localService ? 0 : 1, bl = b.localService ? 0 : 1;
      if (al !== bl) return al - bl;
      var q = function (v) { return /google|natural|neural|premium|enhanced|siri/i.test(v.name) ? 0 : 1; };
      if (q(a) !== q(b)) return q(a) - q(b);
      return a.name.localeCompare(b.name);
    });
    return pool[0];
  }

  // ── HD (server) voices live in the SAME list ──────────────────────────────
  // One control, not two. /document-reader learned this the same way: a unified
  // voice list rather than a second card grid beside it. An HD option is marked
  // by the `hd:` prefix on its value — the only thing that distinguishes the two
  // lanes anywhere in this module.
  var HD_PREFIX = 'hd:';
  function hdIdOf(value) {
    return (typeof value === 'string' && value.indexOf(HD_PREFIX) === 0) ? value.slice(HD_PREFIX.length) : null;
  }
  function selectedHdVoice() {
    return els.voice ? hdIdOf(els.voice.value) : null;
  }

  // Populate the voice <select> for the current text language.
  function loadVoices() {
    if (!els.voice) return;
    var pool = (synth && PROBE && PROBE.getVoicesForLang) ? PROBE.getVoicesForLang(cfg.langPrefix) : [];
    // if no lang-matched voices, show all so the user can still choose
    if (!pool.length && synth) pool = synth.getVoices() || [];
    var hd = (window.CRHd && window.CRHd.available(cfg.langPrefix)) ? window.CRHd.voices(cfg.langPrefix) : [];
    var prev = els.voice.value;
    els.voice.innerHTML = '';

    if (hd.length) {
      var gHd = document.createElement('optgroup');
      gHd.label = t('voice_group_hd', 'HD voices');
      hd.forEach(function (v) {
        var o = document.createElement('option');
        o.value = HD_PREFIX + v.id;
        o.textContent = v.label + (v.gender === 'F' ? ' ♀' : v.gender === 'M' ? ' ♂' : '');
        gHd.appendChild(o);
      });
      els.voice.appendChild(gHd);
    }

    var gDev = hd.length ? document.createElement('optgroup') : els.voice;
    if (hd.length) gDev.label = t('voice_group_device', 'Device voices');
    pool.forEach(function (v) {
      var o = document.createElement('option');
      o.value = v.voiceURI;
      o.textContent = v.name + (v.localService ? '' : ' · ' + t('voice_online', 'online')) + ' (' + v.lang + ')';
      gDev.appendChild(o);
    });
    if (hd.length) els.voice.appendChild(gDev);

    // The device voice stays the DEFAULT: it is instant, works offline and is
    // the only lane that can highlight word by word. HD is a deliberate choice,
    // so a remembered choice is honoured but never invented.
    if (prev) els.voice.value = prev;
    if (!els.voice.value && pool.length) els.voice.value = pool[0].voiceURI;
    els.voice.disabled = (pool.length + hd.length) === 0;
    if (onVoicesCb) { try { onVoicesCb(); } catch (e) {} }
  }

  // ── Highlight helpers (container-scroll only) ──
  function clearHighlights() {
    if (!els.reading) return;
    var a = els.reading.querySelectorAll('.cr-sentence.active,.cr-word.active');
    for (var i = 0; i < a.length; i++) a[i].classList.remove('active');
  }
  function highlightSentence(i) {
    if (!els.reading) return;
    clearHighlights();
    var s = sentences[i];
    if (!s || !s.el) return;
    s.el.classList.add('active');
    // scroll inside the reading container only
    try {
      var c = els.reading;
      var target = s.el.offsetTop - c.offsetTop - (c.clientHeight / 2) + (s.el.offsetHeight / 2);
      c.scrollTo({ top: Math.max(0, target), behavior: window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth' });
    } catch (e) {}
  }
  function highlightWord(sentenceEl, charIndex) {
    var words = sentenceEl.querySelectorAll('.cr-word');
    var hit = null;
    for (var i = 0; i < words.length; i++) {
      if (parseInt(words[i].dataset.start, 10) <= charIndex) hit = words[i]; else break;
    }
    for (var j = 0; j < words.length; j++) words[j].classList.remove('active');
    if (hit) hit.classList.add('active');
  }


  // ── speak one sentence ──
  function speakCurrent() {
    if (currentIndex >= sentences.length) { finish(); return; }
    var s = sentences[currentIndex];
    highlightSentence(currentIndex);

    var rate = els.speed ? parseFloat(els.speed.value) || 1 : 1;

    if (!synth) { finish(); return; }

    var u = new SpeechSynthesisUtterance(s.text);
    // Own phantom-speech handling (SpeechEngineProbe watchdog below) — opt out of
    // the global legacy safety net in timbrica-core.js, whose slower rescue would
    // speak on top of the fallback this tool plays itself.
    u.__tmbManaged = 1;
    utterance = u;                                   // hold ref (GC guard)
    u.lang = cfg.speechLang || (cfg.langPrefix + '-' + cfg.langPrefix.toUpperCase());
    u.rate = rate;
    var v = pickVoice(cfg.langPrefix);
    if (v) u.voice = v;

    // watchdog for phantom engines. A cold engine (first utterance after play,
    // resume or a voice change) gets the measured cold budget — Android Chrome
    // takes seconds to wake its engine, and the old 1500 ms default declared it
    // dead. A miss is retried once with the SAME voice (cancel() also revives an
    // engine stuck mid-sentence) before onPhantom may swap the voice.
    var cold = coldNext, idx = currentIndex;
    if (PROBE && PROBE.makeWatchdog) {
      watchdog = PROBE.makeWatchdog({
        onStartTimeout: PROBE.startBudget ? PROBE.startBudget(v, cold) : 1500,
        expectedDurationMs: PROBE.estimateDurationMs(s.text, rate),
        onPhantom: function (reason) {
          if (!isPlaying) return;
          if (retriedAt !== idx) {
            retriedAt = idx; coldNext = true;
            ae('speech_retry', reason + (cold ? ':cold' : ''));
            try { synth.cancel(); } catch (e) {}
            speakCurrent();
            return;
          }
          onPhantom(reason);
        }
      });
      watchdog.arm();
    }

    u.onstart = function () { coldNext = false; if (watchdog) watchdog.markStart(); };
    u.onboundary = function (e) {
      if (!isPlaying || e.name !== 'word') return;
      boundaryFired = true;
      if (s.el) highlightWord(s.el, e.charIndex || 0);
    };
    u.onend = function () {
      if (watchdog) watchdog.disarm();
      if (!isPlaying) return;
      retriedAt = -1;
      currentIndex++;
      speakCurrent();
    };
    u.onerror = function (e) {
      var err = (e && e.error) || '';
      if (err === 'canceled' || err === 'interrupted') return;   // normal on pause/stop
      if (watchdog) watchdog.disarm();
      onPhantom('synth_error:' + err);
    };

    if (window.__speechKeepAlive) window.__speechKeepAlive(u);
    try { synth.speak(u); } catch (e) { onPhantom('thrown'); }
  }

  // phantom / error → try the next same-lang voice once, else surface honestly
  function onPhantom(reason) {
    try { if (window.__ae) window.__ae('tool_error', { slug: 'camera-reader', error_type: 'phantom_speech', reason: String(reason).slice(0, 60) }); } catch (e) {}
    var lp = cfg.langPrefix;
    triedVoices[lp] = (triedVoices[lp] || 0) + 1;
    var pool = (PROBE && PROBE.getVoicesForLang) ? PROBE.getVoicesForLang(lp) : [];
    if (triedVoices[lp] < pool.length && els.voice) {
      // advance to next voice in the list and retry this sentence
      var idx = 0;
      for (var i = 0; i < els.voice.options.length; i++) if (els.voice.options[i].value === els.voice.value) { idx = i; break; }
      var next = els.voice.options[(idx + 1) % els.voice.options.length];
      if (next) els.voice.value = next.value;
      coldNext = true;
      try { synth.cancel(); } catch (e) {}
      speakCurrent();
      return;
    }
    // exhausted — stop and tell the user their device has no working voice
    stop();
    if (window.__toast) window.__toast(t('tts_unavailable', 'Text-to-speech is unavailable on this device or language. The text above can still be read with your screen reader.'), 'warning');
    announce(t('tts_unavailable', 'Text-to-speech is unavailable on this device.'));
  }

  function finish() {
    isPlaying = false; isPaused = false;
    currentIndex = 0;
    clearHighlights();
    showReading(false);
    setBtn();
    if (onStateCb) onStateCb('ended');
  }

  // ── the HD lane ───────────────────────────────────────────────────────────
  // Everything the server voice needs that the device voice does not: it is
  // fetched, so it can be refused, and it carries no word timings, so the
  // highlight is per CHUNK (a real boundary) instead of per word.
  var hdMode = false;

  function hdRate() { return els.speed ? parseFloat(els.speed.value) || 1 : 1; }

  function playHd(voiceId) {
    hdMode = true;
    isPlaying = true; isPaused = false;
    showReading(true);
    setBtn();
    clearHighlights();

    // The voice is made on our server, and that takes about three seconds even
    // for a short first piece (measured: a 350-character request is 3.4 s). The
    // person who pressed this cannot see a spinner, so SAY that it is coming —
    // announcing "reading aloud" here would be a lie for those three seconds,
    // and silence is what "the button does nothing" feels like.
    var preparing = t('hd_preparing', 'Preparing the voice…');
    announce(preparing);
    if (els.notice) { els.notice.textContent = preparing; els.notice.hidden = false; }

    var started = false;
    var full = sentences.map(function (s) { return s.text; }).join(' ');
    window.CRHd.speak(full, voiceId, {
      rate: hdRate(),
      onChunk: function (i, total) {
        if (!started) {
          started = true;
          if (els.notice) els.notice.hidden = true;
          announce(t('reading', 'Reading aloud'));
        }
        // Only meaningful when the passage needed more than one request; a
        // single-chunk read has nothing honest to point at.
        if (total > 1) highlightChunkRange(i, total);
      },
      onEnd: function () { hdMode = false; finish(); },
      onError: function (err) { hdFallback(err); },
    }).catch(function (err) { hdFallback(err); });
  }

  /**
   * Approximate which sentences belong to chunk i. The chunk boundary itself is
   * real (we asked for exactly that text); the mapping back to sentence indices
   * is by position in the joined string, which is exact for the boundaries and
   * therefore safe to highlight.
   */
  function highlightChunkRange(i, total) {
    var per = Math.ceil(sentences.length / total);
    var from = i * per, to = Math.min(sentences.length, from + per);
    clearHighlights();
    for (var k = from; k < to; k++) {
      if (sentences[k] && sentences[k].el) sentences[k].el.classList.add('active');
    }
    if (sentences[from] && sentences[from].el) highlightSentence(from);
  }

  /**
   * The server voice could not finish. A blind user pressed "read aloud" and
   * must not be met with silence, so the device voice takes over from where the
   * server stopped, and the reason is said ONCE — quietly, not as an error the
   * person has to act on.
   */
  var hdToldOnce = false;
  function hdFallback(err) {
    var kind = (err && err.kind) || 'refused';
    hdMode = false;
    try { window.CRHd.stop(); } catch (e) {}
    if (els.notice) els.notice.hidden = true;   // drop the preparing line
    ae('hd_voice_fallback', kind);

    // Move the picker back to a device voice so the next press does not queue
    // the same refusal again.
    if (els.voice) {
      var opts = els.voice.querySelectorAll('option');
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].value.indexOf('hd:') !== 0) { els.voice.value = opts[i].value; break; }
      }
    }

    if (!hdToldOnce) {
      hdToldOnce = true;
      // Only two refusals are worth their own sentence, because only they tell
      // the person something they can act on: the daily pot and the per-minute
      // rate. Everything else is "it did not work, you are still being read to".
      // ⚠️ t(k, '') would hand back the KEY when the string is missing — t()
      // falls through to the key itself — so read i18n directly here.
      var specific = (cfg.i18n && cfg.i18n['hd_' + kind]) || '';
      var msg = specific || t('hd_unavailable', 'Switched to the device voice.');
      if (window.__toast) window.__toast(msg, 'info');
      announce(msg);
    }

    // Finish the passage on the device voice. A remainder means part of it has
    // already been spoken aloud by the server voice.
    if (err && err.remainder) {
      var spoken = sentences.length - splitCount(err.remainder);
      currentIndex = Math.max(0, Math.min(sentences.length - 1, spoken));
    } else {
      currentIndex = 0;
    }
    if (!synth) { finish(); return; }
    isPlaying = true; isPaused = false;
    setBtn();
    coldNext = true;
    speakCurrent();
  }

  function splitCount(text) { return splitSentences(text).length; }

  function ae(f, kind) {
    try { window.__ae && window.__ae('feature_use', { slug: 'camera-reader', f: f, kind: kind }); } catch (e) {}
  }

  // ── public controls ──
  function play() {
    if (!sentences.length) return;
    // resume from pause
    if (isPaused) {
      isPaused = false; isPlaying = true; setBtn();
      if (hdMode) { window.CRHd.resume(); return; }
      coldNext = true; speakCurrent(); return;
    }
    if (isPlaying) return;
    if (window.__audioGate) { try { var g = window.__audioGate.element(); window.__audioGate.unlockNow(g); } catch (e) {} }

    var hdVoice = selectedHdVoice();
    if (hdVoice && window.CRHd) { playHd(hdVoice); return; }

    isPlaying = true; isPaused = false;
    boundaryFired = false;
    if (currentIndex >= sentences.length) currentIndex = 0;
    showReading(true);
    setBtn();
    coldNext = true;
    speakCurrent();
    announce(t('reading', 'Reading aloud'));
  }

  // cancel-based pause: synth.pause() leaves speaking=true / wedges iOS
  function pause() {
    if (!isPlaying) return;
    isPaused = true; isPlaying = false;
    if (hdMode) { try { window.CRHd.pause(); } catch (e) {} setBtn(); return; }
    if (watchdog) watchdog.disarm();
    try { synth && synth.cancel(); } catch (e) {}
    setBtn();
  }

  function stop() {
    isPlaying = false; isPaused = false;
    currentIndex = 0;
    if (hdMode) { hdMode = false; try { window.CRHd.stop(); } catch (e) {} if (els.notice) els.notice.hidden = true; }
    if (watchdog) watchdog.disarm();
    try { synth && synth.cancel(); } catch (e) {}
    clearHighlights();
    showReading(false);
    setBtn();
    if (onStateCb) onStateCb('stopped');
  }

  function toggle() { if (isPlaying) pause(); else play(); }

  function setBtn() {
    if (els.play) {
      var playing = isPlaying;
      els.play.setAttribute('aria-pressed', playing ? 'true' : 'false');
      els.play.dataset.state = playing ? 'playing' : (isPaused ? 'paused' : 'idle');
      var lbl = els.play.querySelector('[data-label]');
      if (lbl) lbl.textContent = playing ? t('pause', 'Pause') : (isPaused ? t('resume', 'Resume') : t('play', 'Read aloud'));
    }
    if (els.stop) els.stop.style.display = (isPlaying || isPaused) ? '' : 'none';
  }

  // ── set the text to read (called after OCR) ──
  function setText(text, langPrefix, speechLang) {
    stop();
    cfg.langPrefix = (langPrefix || cfg.langPrefix || 'en').split('-')[0];
    cfg.speechLang = speechLang || (cfg.langPrefix + '-' + cfg.langPrefix.toUpperCase());
    sentences = splitSentences(text).map(function (s) { return { text: s, el: null }; });
    render();
    showReading(false);   // show the editable textarea until the user hits play
    loadVoices();
    setBtn();
  }

  function clear() {
    stop();
    sentences = [];
    if (els.reading) els.reading.innerHTML = '';
  }

  function isActive() { return isPlaying || isPaused; }

  function init(config) {
    cfg.i18n = (config && config.i18n) || {};
    els = (config && config.els) || {};
    onStateCb = (config && config.onState) || null;
    onVoicesCb = (config && config.onVoicesLoaded) || null;

    // NOT `if (synth)`: the HD voices come from our server, so the list has
    // something to show even on a browser with no speechSynthesis at all — and
    // that is exactly the browser where an HD voice matters most.
    try { loadVoices(); } catch (e) {}
    if (synth) { try { synth.onvoiceschanged = function () { loadVoices(); }; } catch (e) {} }

    if (els.play) els.play.addEventListener('click', toggle);
    if (els.stop) els.stop.addEventListener('click', stop);
    if (els.speed) {
      var applySpeed = function () {
        var v = parseFloat(els.speed.value).toFixed(1);
        if (els.speedVal) els.speedVal.textContent = v + '×';
        try { window.__toolSettings && window.__toolSettings.save('camera-reader', 'speed', v); } catch (e) {}
        // On the HD lane the rate is baked into the synthesis request, so a
        // mid-read change cannot apply without buying the same page a second
        // time out of the daily character pot. It takes effect on the next
        // press instead — silently, because the slider already moved.
        if (hdMode) return;
        // rate change on a live utterance silently stops Chrome → restart current sentence
        if (isPlaying) { try { synth && synth.cancel(); } catch (e) {} clearTimeout(applySpeed._t); applySpeed._t = setTimeout(function () { if (isPlaying) speakCurrent(); }, 250); }
      };
      els.speed.addEventListener('input', applySpeed);
    }
    if (els.voice) els.voice.addEventListener('change', function () {
      try { window.__toolSettings && window.__toolSettings.save('camera-reader', 'voice', els.voice.value); } catch (e) {}
      if (isPlaying) { try { synth && synth.cancel(); } catch (e) {} speakCurrent(); }
    });

    // tap a word/sentence in the reading surface → start reading from there
    if (els.reading) els.reading.addEventListener('click', function (e) {
      var sEl = e.target.closest && e.target.closest('.cr-sentence');
      if (!sEl) return;
      currentIndex = parseInt(sEl.dataset.index, 10) || 0;
      isPaused = false;
      if (!isPlaying) play(); else { try { synth && synth.cancel(); } catch (_) {} speakCurrent(); }
    });

    // MediaSession (lock-screen / bluetooth keys)
    try {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', play);
        navigator.mediaSession.setActionHandler('pause', pause);
        navigator.mediaSession.setActionHandler('stop', stop);
      }
    } catch (e) {}
  }

  window.CRRead = { init: init, setText: setText, play: play, pause: pause, stop: stop, toggle: toggle, isActive: isActive, clear: clear, loadVoices: loadVoices };
})();
